'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { type SortPillOption, SortPillRow } from '@/components/shared/sort-pill-row';
import { CompetitionAboutSheet } from '@/components/social/competition/competition-about-sheet';
import { CompetitionHero } from '@/components/social/competition/competition-hero';
import { CompetitionsListEmpty, CompetitionsListError, CompetitionsListLoading } from '@/components/social/competition/competitions-list-boundaries';
import type { TradingCompetitionSummary } from '@/components/social/competition/types';
import { ChampionIconSolid, CheckmarkCircle02IconSolid, Clock04IconSolid, Layers01IconSolid } from '@/components/ui/icon/glyphs';
import { competitionPath } from '@/lib/competitions/paths';
import type { CompetitionsPhaseFilter } from '@/lib/competitions/query-args';
import { useAppRouter } from '@/lib/network/use-app-router';
export type CompetitionsListStatus = 'ready' | 'loading' | 'error' | 'empty';
export interface CompetitionsListProps {
  status: CompetitionsListStatus;
  initialPhase: CompetitionsPhaseFilter;
  initialNowMs: number | null;
  timeZone: string;
  /** Every published competition: Live by soonest end, Upcoming by soonest start, Concluded by latest end. */
  competitions: readonly TradingCompetitionSummary[];
}
function isPhaseFilter(value: string): value is CompetitionsPhaseFilter {
  return value === 'all' || value === 'live' || value === 'upcoming' || value === 'concluded';
}
export function CompetitionsList(props) {
  const $ = _c(65);
  const t = useTranslations("competitions.list");
  const router = useAppRouter();
  const [phase, setPhase] = useState(props.initialPhase);
  const [aboutId, setAboutId] = useState(null);
  const t0 = props.competitions.length;
  let t1;
  if ($[0] !== props.competitions) {
    t1 = props.competitions.filter(_temp);
    $[0] = props.competitions;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = t1.length;
  let t3;
  if ($[2] !== props.competitions) {
    t3 = props.competitions.filter(_temp2);
    $[2] = props.competitions;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = t3.length;
  let t5;
  if ($[4] !== props.competitions) {
    t5 = props.competitions.filter(_temp3);
    $[4] = props.competitions;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== props.competitions.length || $[7] !== t1.length || $[8] !== t3.length || $[9] !== t5.length) {
    t6 = {
      all: t0,
      live: t2,
      upcoming: t4,
      concluded: t5.length
    };
    $[6] = props.competitions.length;
    $[7] = t1.length;
    $[8] = t3.length;
    $[9] = t5.length;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const counts = t6;
  let t7;
  if ($[11] !== phase || $[12] !== props.competitions) {
    t7 = phase === "all" ? props.competitions : props.competitions.filter(item_2 => item_2.phase === phase);
    $[11] = phase;
    $[12] = props.competitions;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const visible = t7;
  let t8;
  if ($[14] !== aboutId || $[15] !== props.competitions) {
    t8 = props.competitions.find(item_3 => item_3.id === aboutId) ?? null;
    $[14] = aboutId;
    $[15] = props.competitions;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const about = t8;
  let t9;
  if ($[17] !== t) {
    t9 = t("all");
    $[17] = t;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const t10 = `${t9} ${counts.all}`;
  let t11;
  if ($[19] !== t10) {
    t11 = {
      value: "all",
      label: t10,
      prefix: Layers01IconSolid
    };
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t) {
    t12 = t("live");
    $[21] = t;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const t13 = `${t12} ${counts.live}`;
  let t14;
  if ($[23] !== t13) {
    t14 = {
      value: "live",
      label: t13,
      prefix: ChampionIconSolid
    };
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t) {
    t15 = t("upcoming");
    $[25] = t;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  const t16 = `${t15} ${counts.upcoming}`;
  let t17;
  if ($[27] !== t16) {
    t17 = {
      value: "upcoming",
      label: t16,
      prefix: Clock04IconSolid
    };
    $[27] = t16;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== t) {
    t18 = t("concluded");
    $[29] = t;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  const t19 = `${t18} ${counts.concluded}`;
  let t20;
  if ($[31] !== t19) {
    t20 = {
      value: "concluded",
      label: t19,
      prefix: CheckmarkCircle02IconSolid
    };
    $[31] = t19;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] !== t11 || $[34] !== t14 || $[35] !== t17 || $[36] !== t20) {
    t21 = [t11, t14, t17, t20];
    $[33] = t11;
    $[34] = t14;
    $[35] = t17;
    $[36] = t20;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  const phaseOptions = t21;
  let t22;
  if ($[38] !== phase || $[39] !== t) {
    t22 = phase === "live" ? t("live") : phase === "upcoming" ? t("upcoming") : phase === "concluded" ? t("concluded") : t("all");
    $[38] = phase;
    $[39] = t;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  const phaseLabel = t22;
  let t23;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = function changePhase(value) {
      if (isPhaseFilter(value)) {
        setPhase(value);
      }
    };
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  const changePhase = t23;
  let t24;
  if ($[42] !== t) {
    t24 = t("title");
    $[42] = t;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== t24) {
    t25 = <h1 className="sr-only">{t24}</h1>;
    $[44] = t24;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== phase || $[47] !== phaseOptions) {
    t26 = <SortPillRow options={phaseOptions} activeValue={phase} onChange={changePhase} layoutId="competitions-phase-pill" />;
    $[46] = phase;
    $[47] = phaseOptions;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] !== phaseLabel || $[50] !== props.competitions.length || $[51] !== props.initialNowMs || $[52] !== props.status || $[53] !== props.timeZone || $[54] !== router || $[55] !== visible) {
    t27 = props.status === "loading" ? <CompetitionsListLoading /> : props.status === "error" ? <CompetitionsListError onRetry={() => router.refresh()} /> : props.status === "empty" || props.competitions.length === 0 ? <CompetitionsListEmpty kind="empty" phaseLabel={phaseLabel} onShowAll={() => setPhase("all")} /> : visible.length === 0 ? <CompetitionsListEmpty kind="no-matches" phaseLabel={phaseLabel} onShowAll={() => setPhase("all")} /> : <ul className="flex flex-col gap-3" data-testid="competitions-list-rows">{visible.map(competition => <li key={competition.id} className="min-w-0"><CompetitionHero competition={competition} size="compact" initialNowMs={props.initialNowMs} timeZone={props.timeZone} href={competitionPath(competition.id)} onOpenInfo={() => setAboutId(competition.id)} /></li>)}</ul>;
    $[49] = phaseLabel;
    $[50] = props.competitions.length;
    $[51] = props.initialNowMs;
    $[52] = props.status;
    $[53] = props.timeZone;
    $[54] = router;
    $[55] = visible;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  let t28;
  if ($[57] !== about || $[58] !== props.timeZone) {
    t28 = about !== null ? <CompetitionAboutSheet competition={about} timeZone={props.timeZone} open={true} onOpenChange={open => {
      if (!open) {
        setAboutId(null);
      }
    }} /> : null;
    $[57] = about;
    $[58] = props.timeZone;
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  let t29;
  if ($[60] !== t25 || $[61] !== t26 || $[62] !== t27 || $[63] !== t28) {
    t29 = <div className="flex w-full min-w-0 flex-col gap-4 md:gap-6" data-testid="competitions-list">{t25}{t26}{t27}{t28}</div>;
    $[60] = t25;
    $[61] = t26;
    $[62] = t27;
    $[63] = t28;
    $[64] = t29;
  } else {
    t29 = $[64];
  }
  return t29;
}
function _temp3(item_1) {
  return item_1.phase === "concluded";
}
function _temp2(item_0) {
  return item_0.phase === "upcoming";
}
function _temp(item) {
  return item.phase === "live";
}